import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { Dialog, useDialog } from 'components';
import ControlledDataTable from 'components/tables/controlled-data-table';
import {
  ControlledDataTableColumn,
  ControlledDataTableProvider,
  FetchDataArgs
} from 'components/tables/data-table-context';
import { DataTableRef } from 'components/tables/data-table';
import { useResponse } from 'contexts/response';
import { useFleetFiles } from 'hooks';
import { FleetFile } from 'types';
import { LogicalOperator } from 'utils';

import { RowEditButton } from './row-edit-button';
import { SizeTypeFormMode, SizeTypeMappingForm } from './size-type-mapping-form';
import { UnresolvedSizeTypes } from './unresolved-size-types';

const RESOLUTION_LABELS: Record<FleetFile.SizeTypeResolution, string> = {
  UNRESOLVED: 'Not reviewed',
  NO_TARGET: 'No single size type',
  MAPPED_ACTIVE: 'Suggests a size type',
  MAPPED_INACTIVE: 'Suggestion retired'
};

/*
 * Settings > Fleet Files > Size Type Mappings: the provider size-type code -> catalog crosswalk.
 *
 * Each row decides what the gate lookup SUGGESTS for a provider code. The gate user is responsible
 * for entering what is actually in the yard, and their entry is what gets saved; editing a row here
 * changes future suggestions only. Edits happen one row at a time in a dialog, because the class,
 * target and review note of a row are linked (design D1).
 */
const SizeTypeMappings: FC = () => {
  const { getSizeTypeMappings } = useFleetFiles();
  const { setSuccess } = useResponse();
  const { ref, openDialog, closeDialog } = useDialog();
  const tableRef = useRef<DataTableRef>(null);

  const [resolution, setResolution] = useState<FleetFile.SizeTypeResolution | ''>('');
  const [mode, setMode] = useState<SizeTypeFormMode>();
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchData = useCallback(
    (args: FetchDataArgs) =>
      getSizeTypeMappings({
        filterString: args.filterString,
        resolution: resolution || undefined,
        pageNum: args.pageNum,
        pageSize: args.pageSize,
        sortField: args.sortField,
        sortDirection: args.sortDirection
      }),
    [getSizeTypeMappings, resolution]
  );

  const reloadRow = useCallback(
    async (code: number) =>
      (await getSizeTypeMappings({ filterString: `code==${code}`, pageNum: 0, pageSize: 1 })).content?.[0],
    [getSizeTypeMappings]
  );

  const open = useCallback(
    (next: SizeTypeFormMode) => {
      setMode(next);
      openDialog();
    },
    [openDialog]
  );

  const openByCode = useCallback(
    async (code: number) => {
      const row = await reloadRow(code);
      if (row) open({ kind: 'edit', row });
    },
    [reloadRow, open]
  );

  const close = useCallback(() => {
    closeDialog();
    setMode(undefined);
  }, [closeDialog]);

  /* re-read the grid and the unresolved list: after a save, or once a 409 says they are out of date */
  const refresh = useCallback(() => {
    tableRef.current?.forceFetch();
    setRefreshKey(k => k + 1);
  }, []);

  const onSaved = useCallback(
    (row: FleetFile.SizeType) => {
      close();
      setSuccess(`Fleet size type code ${row.code} saved`);
      refresh();
    },
    [close, setSuccess, refresh]
  );

  const columns: ControlledDataTableColumn<FleetFile.SizeType>[] = useMemo(
    () => [
      {
        field: 'code',
        label: 'PROVIDER CODE',
        type: 'number',
        /*
         * The edit action lives in the first column, not only in the trailing one: the grid is
         * wider than the card, so the trailing pencil is off-screen until the admin scrolls sideways.
         */
        renderCell: (code: number, row: FleetFile.SizeType) => (
          <RowEditButton label={`Edit provider code ${code}`} onClick={() => open({ kind: 'edit', row })}>
            {code}
          </RowEditButton>
        )
      },
      { field: 'description', label: 'PROVIDER DESCRIPTION', type: 'string' },
      {
        field: 'targetSizeType',
        label: 'SUGGESTS',
        sortable: false,
        filterable: false,
        skipQuickSearch: true,
        getValueLabel: (target: FleetFile.SizeTypeTarget | null | undefined) =>
          target ? `${target.code} — ${target.label}${target.active ? '' : ' (retired)'}` : ''
      },
      { field: 'equipmentType', label: 'CLASS', skipQuickSearch: true, filterable: false },
      {
        field: 'resolution',
        label: 'STATE',
        sortable: false,
        filterable: false,
        skipQuickSearch: true,
        getValueLabel: (value: FleetFile.SizeTypeResolution) => RESOLUTION_LABELS[value] ?? value
      },
      { field: 'reviewNote', label: 'NOTE', type: 'string' },
      {
        field: 'sighting',
        label: 'SEEN AT GATE',
        sortable: false,
        filterable: false,
        skipQuickSearch: true,
        getValueLabel: (sighting: FleetFile.SizeType['sighting']) => (sighting ? `${sighting.seenCount}` : '')
      },
      { field: 'iso', label: 'ISO ALIASES', type: 'string' },
      /* `sizeType` is the legacy string: shown for traceability, never a suggestion */
      { field: 'sizeType', label: 'LEGACY VALUE (NOT USED)', type: 'string' },
      { field: 'lastUpdatedBy', label: 'LAST EDITED BY', type: 'string', skipQuickSearch: true }
    ],
    [open]
  );

  const initialColumns: string[] = useMemo(() => columns.map(({ field }) => field), [columns]);

  return (
    <>
      <Dialog ref={ref} closeDialog={close}>
        {!!mode && (
          <SizeTypeMappingForm
            key={mode.kind === 'edit' ? `${mode.row.id}-${mode.row.version}` : `new-${mode.code ?? ''}`}
            mode={mode}
            onSaved={onSaved}
            onCancel={close}
            reloadRow={reloadRow}
            onStale={refresh}
          />
        )}
      </Dialog>
      <div
        className="card-primary d-flex flex-column flex-fill overflow-hidden"
        style={{ marginTop: 0, minHeight: 320 }}
      >
        <div className="card-header p-relative">
          <h4 className="p-absolute" style={{ top: 0, left: '50%', translate: '-50%' }}>
            SIZE TYPE MAPPINGS
          </h4>
          <button
            type="button"
            className="btn-primary py-2 px-4 p-absolute"
            style={{ top: 4, right: 12 }}
            onClick={() => open({ kind: 'create' })}
          >
            ADD PROVIDER CODE
          </button>
        </div>
        <div className="card-body d-flex flex-column flex-fill overflow-hidden">
          <ControlledDataTableProvider initialColumns={initialColumns} initialPagination={{ sortField: 'code' }}>
            <ControlledDataTable<FleetFile.SizeType>
              columns={columns}
              dataTableRef={tableRef}
              fetchData={fetchData}
              handleEdit={row => open({ kind: 'edit', row })}
              searchLogicalOperator={LogicalOperator.AND}
              tableToolbar
            >
              {/* on the search line, so both grids' headers have the same shape */}
              <select
                aria-label="Filter by state"
                /* auto right margin keeps it beside the search; the toolbar otherwise pushes it under the header button */
                style={{ marginLeft: 12, marginRight: 'auto' }}
                value={resolution}
                onChange={({ target }) => setResolution(target.value as FleetFile.SizeTypeResolution | '')}
              >
                <option value="">All codes</option>
                {(Object.keys(RESOLUTION_LABELS) as FleetFile.SizeTypeResolution[]).map(state => (
                  <option key={state} value={state}>
                    {RESOLUTION_LABELS[state]}
                  </option>
                ))}
              </select>
            </ControlledDataTable>
          </ControlledDataTableProvider>
        </div>
      </div>
      <UnresolvedSizeTypes refreshKey={refreshKey} onAdd={code => open({ kind: 'create', code })} onOpen={openByCode} />
    </>
  );
};

export default SizeTypeMappings;
